// Step 2 of the property wizard — "Đặc điểm".
//
// Fields:
//   - area               (required, m²)
//   - floor              (numeric)
//   - axis               (text — "Trục", the unit's stack: "05A", "12")
//   - totalFloors        (numeric)
//   - bedrooms           (chip picker — Studio / 1 / 2 / 3 / 4+)
//   - bathrooms          (numeric)
//   - direction          (select)
//   - price              (required, with auto-unit hint by listingType)
//   - furniture          (chip picker)
//   - description        (textarea)

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  BEDROOM_OPTIONS,
  BROKERAGE_FEE_TYPE_OPTIONS,
  PROPERTY_DIRECTION_OPTIONS,
  PROPERTY_FURNITURE_OPTIONS,
} from "@/features/property/utils/property-enum-options";
import {
  WizardChipPicker,
  WizardField,
} from "@/features/property/wizard/wizard-field";
import { useProject } from "@/features/project/api";
import { cn } from "@/lib/utils";
import type {
  PropertyDraft,
  StepValidation,
} from "@/features/property/wizard/wizard-state";

interface DetailsStepProps {
  draft: PropertyDraft;
  onChange: (next: Partial<PropertyDraft>) => void;
  validation: StepValidation;
}

export function DetailsStep({ draft, onChange, validation }: DetailsStepProps) {
  const errors = validation.errors;
  const priceUnit = draft.listingType === "RENT" ? "tr/th" : "tỷ";

  // Total floors is a shared project field — locked + prefilled when the unit
  // is linked to a project that defines it.
  const linkedProject = useProject(draft.projectId).data ?? null;
  const floorsLocked =
    draft.projectId != null && linkedProject?.totalFloors != null;

  // Match the design comp: price is entered as "tỷ" (billions) for SALE
  // or "triệu/tháng" for RENT. Convert to the stored VND base on save.
  const priceMultiplier = draft.listingType === "RENT" ? 1_000_000 : 1_000_000_000;
  const priceDisplay =
    draft.price !== undefined
      ? String(draft.price / priceMultiplier)
      : "";

  // Brokerage fee: AMOUNT is entered in "triệu" (× 1e6 on store); MONTHS and
  // PERCENT are stored as-is. The value field clears when the type changes.
  const feeMultiplier = draft.brokerageFeeType === "AMOUNT" ? 1_000_000 : 1;
  const feeUnit =
    draft.brokerageFeeType === "MONTHS"
      ? "tháng"
      : draft.brokerageFeeType === "PERCENT"
        ? "%"
        : "triệu";
  const feeDisplay =
    draft.brokerageFeeValue !== undefined
      ? String(draft.brokerageFeeValue / feeMultiplier)
      : "";

  return (
    <div className="flex flex-col gap-6">
      <h2 className="border-b border-border pb-2 text-[16px] font-bold tracking-tight">
        Đặc điểm bất động sản
      </h2>

      {/* 2×2: area + floor / axis + totalFloors (was 3-up before axis). */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <WizardField
          label="Diện tích (m²)"
          required
          {...(errors.area !== undefined ? { error: errors.area } : {})}
        >
          {(id) => (
            <Input
              id={id}
              type="number"
              inputMode="decimal"
              min={0}
              placeholder="85"
              value={draft.area ?? ""}
              onChange={(e) => onChange({ area: parseNumber(e.target.value) })}
            />
          )}
        </WizardField>

        <WizardField label="Tầng">
          {(id) => (
            <Input
              id={id}
              type="number"
              inputMode="numeric"
              min={0}
              placeholder="12"
              value={draft.floor ?? ""}
              onChange={(e) => onChange({ floor: parseInt(e.target.value) })}
            />
          )}
        </WizardField>

        {/* "Trục" — the unit's stack/axis. Tầng + trục = the exact apartment,
            so the backend shows it only to the assigned Source / managers. */}
        <WizardField label="Trục" hint="Chỉ Nguồn được gán & quản lý thấy">
          {(id) => (
            <Input
              id={id}
              type="text"
              maxLength={10}
              placeholder="05A"
              value={draft.axis ?? ""}
              onChange={(e) => onChange({ axis: e.target.value })}
            />
          )}
        </WizardField>

        <WizardField
          label="Tổng số tầng"
          {...(floorsLocked ? { hint: "Từ dự án" } : {})}
        >
          {(id) =>
            floorsLocked ? (
              <Input
                id={id}
                type="number"
                value={draft.totalFloors ?? ""}
                disabled
                readOnly
                aria-label="Tổng số tầng"
              />
            ) : (
              <Input
                id={id}
                type="number"
                inputMode="numeric"
                min={0}
                placeholder="30"
                value={draft.totalFloors ?? ""}
                onChange={(e) => onChange({ totalFloors: parseInt(e.target.value) })}
              />
            )
          }
        </WizardField>
      </div>

      <WizardField label="Số phòng ngủ">
        <WizardChipPicker
          ariaLabel="Số phòng ngủ"
          tone="ink"
          options={BEDROOM_OPTIONS}
          {...(draft.bedrooms !== undefined ? { value: draft.bedrooms } : {})}
          onChange={(v) => onChange({ bedrooms: v })}
        />
      </WizardField>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <WizardField label="Số phòng tắm">
          {(id) => (
            <Input
              id={id}
              type="number"
              inputMode="numeric"
              min={0}
              placeholder="2"
              value={draft.bathrooms ?? ""}
              onChange={(e) => onChange({ bathrooms: parseInt(e.target.value) })}
            />
          )}
        </WizardField>

        <WizardField label="Hướng">
          <Select
            value={draft.direction ?? ""}
            onValueChange={(v) =>
              onChange({
                direction: (v === ""
                  ? undefined
                  : (v as NonNullable<PropertyDraft["direction"]>)),
              })
            }
          >
            <SelectTrigger aria-label="Hướng">
              <SelectValue placeholder="Chọn hướng" />
            </SelectTrigger>
            <SelectContent>
              {PROPERTY_DIRECTION_OPTIONS.map((d) => (
                <SelectItem key={d.value} value={d.value}>
                  {d.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </WizardField>
      </div>

      <WizardField
        label="Giá"
        required
        {...(errors.price !== undefined ? { error: errors.price } : {})}
      >
        {(id) => (
          <div className="relative">
            <Input
              id={id}
              type="number"
              inputMode="decimal"
              min={0}
              step="0.1"
              placeholder={draft.listingType === "RENT" ? "18" : "5.2"}
              value={priceDisplay}
              onChange={(e) =>
                onChange({
                  price:
                    e.target.value === ""
                      ? undefined
                      : Number(e.target.value) * priceMultiplier,
                })
              }
              className="pr-14"
            />
            <span
              className={cn(
                "pointer-events-none absolute top-1/2 right-3 -translate-y-1/2",
                "font-mono text-[12px] text-muted-foreground",
              )}
            >
              {priceUnit}
            </span>
          </div>
        )}
      </WizardField>

      <WizardField label="Nội thất">
        <WizardChipPicker
          ariaLabel="Nội thất"
          options={PROPERTY_FURNITURE_OPTIONS}
          {...(draft.furniture !== undefined ? { value: draft.furniture } : {})}
          onChange={(v) => onChange({ furniture: v })}
        />
      </WizardField>

      {/* Brokerage fee — the commission the agent earns on a deal. Internal-
          only; never shown on the public share page. */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <WizardField label="Phí môi giới">
          <Select
            value={draft.brokerageFeeType ?? "NONE"}
            onValueChange={(v) =>
              onChange(
                v === "NONE"
                  ? {
                      brokerageFeeType: undefined,
                      brokerageFeeValue: undefined,
                      clearBrokerageFee: true,
                    }
                  : {
                      brokerageFeeType: v as NonNullable<
                        PropertyDraft["brokerageFeeType"]
                      >,
                      brokerageFeeValue: undefined,
                      clearBrokerageFee: false,
                    },
              )
            }
          >
            <SelectTrigger aria-label="Loại phí môi giới">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="NONE">Không có</SelectItem>
              {BROKERAGE_FEE_TYPE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </WizardField>

        {draft.brokerageFeeType !== undefined ? (
          <WizardField label="Giá trị phí môi giới">
            {(id) => (
              <div className="relative">
                <Input
                  id={id}
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step={draft.brokerageFeeType === "AMOUNT" ? "1" : "0.5"}
                  placeholder={
                    draft.brokerageFeeType === "PERCENT"
                      ? "2"
                      : draft.brokerageFeeType === "MONTHS"
                        ? "1"
                        : "150"
                  }
                  value={feeDisplay}
                  onChange={(e) =>
                    onChange({
                      brokerageFeeValue:
                        e.target.value === ""
                          ? undefined
                          : Number(e.target.value) * feeMultiplier,
                    })
                  }
                  className="pr-14"
                />
                <span
                  className={cn(
                    "pointer-events-none absolute top-1/2 right-3 -translate-y-1/2",
                    "font-mono text-[12px] text-muted-foreground",
                  )}
                >
                  {feeUnit}
                </span>
              </div>
            )}
          </WizardField>
        ) : null}
      </div>

      <WizardField label="Mô tả (tuỳ chọn)">
        {(id) => (
          <Textarea
            id={id}
            placeholder="Mô tả chi tiết về bất động sản…"
            rows={4}
            value={draft.description ?? ""}
            onChange={(e) => onChange({ description: e.target.value })}
          />
        )}
      </WizardField>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Number-input helpers — both return `undefined` for empty/invalid input
// so the draft falls back to the "no value" state, not 0 or NaN.
// ---------------------------------------------------------------------------

function parseNumber(raw: string): number | undefined {
  if (raw === "") return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

function parseInt(raw: string): number | undefined {
  if (raw === "") return undefined;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) ? n : undefined;
}
